import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocalStorage } from 'usehooks-ts';
import { toast } from 'sonner';
import { ChainId } from '@shared/utils/config/networks';
import { sendFundsSchema, SendFundsSchemaInputs } from '@shared/zod-schemas/frontend';
import { ReceiverUserData, ReceiverWalletData, StagesSendFunds } from '@shared/types/frontend/sendFunds';
import { IoIosArrowBack } from 'react-icons/io';
import { Loader2 } from 'lucide-react';
import { ethers } from 'ethers';
import { explorers } from '@shared/types/executor';
import { PiPaperPlaneTilt } from 'react-icons/pi';
import { useSession } from '@/contexts/LitSessionProvider';
import useERC20Balance from '@/hooks/portfolio/useERC20Balance';
import useWithdrawAsset from '@/hooks/transfer/useWithdrawAsset';
import { useWithdrawQuote } from '@/hooks/QuotingEngineCalls/transfer/useWithdrawQuote';
import useGetWrappedWallet from '@/hooks/auth/useGetWrappedWallet';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import SwapLoader from '@/components/trade/SwapLoader';
import SlidingModal from '@/components/home/mobile/SlidingModal';
import VerifyStage from './stages/VerifyStage';
import RecentTransfers from './recentTransfers';
import SuccessStage from './stages/SuccessStage';
import KeypadController from './keypad/KeypadController';
import { toSpecialFixed } from '@/lib/misc/utils';
import { getQuoteAndTransferParams } from './actions';
import UserSearchResults from './userSearchResults';
import FixedBtnWrapper from '@/components/ui/FixBtnWrapper';
import validateSolAddress from '@/lib/misc/validateSolAddress';
interface SendFundsControllerProps {
  tokenData: {
    address: string;
    network: ChainId;
  };
  onlyUsername?: boolean;
  isOpen?: boolean;
  onClose?: (_: boolean) => void;
}
export const _userNameData = async (username: string) => {
  const _url = `/api/db/setProfile?username=${username}`;
  try {
    const _res = await fetch(_url);
    console.log('_userNameData:res', {
      _res
    });
    return _res;
  } catch (error) {
    console.log('_userNameData:error', {
      error
    });
    throw new Error('error _userNameData');
  }
};
const ErrorStage = () => {
  return <div className='grid gap-8 py-[10%] justify-items-center' data-sentry-component="ErrorStage" data-sentry-source-file="SendFundsController.tsx">
      <Image className='mt-[20%] m-auto' alt='error' src='/static/images/idea-error.png' width={68} height={68} data-sentry-element="Image" data-sentry-source-file="SendFundsController.tsx" />
      <h1 className='font-normal text-[36px] text-genius-cream/50'>Not Very Smart</h1>
      <p className='text-sm  text-genius-cream/50 text-center px-12'>
        Failed to Send Funds! This one is on us. Please try again below and if the problem persists,
        please reach out to support at support@tradegenius.com
      </p>
    </div>;
};
const SendFundsController: React.FC<SendFundsControllerProps> = ({
  tokenData,
  onlyUsername = false,
  isOpen = false,
  onClose
}) => {
  const {
    PKP
  } = useSession();
  const {
    data: assetData
  } = useERC20Balance({
    evmAddresses: [PKP?.additionalEthAddress],
    solanaAddresses: [PKP?.solanaAddress],
    useGeniusAPI: true
  });
  const {
    getEVMWallet
  } = useGetWrappedWallet();
  const [stage, setStage] = useState(StagesSendFunds.amount);
  const [isModalOpen, setIsModalOpen] = useState(isOpen ?? false);
  const [receiver, setReceiver] = useState<ReceiverUserData | ReceiverWalletData | undefined>();
  const [isCheckingInput, setIsCheckingInput] = useState(false);
  const [recentUsers, setRecentUsers] = useLocalStorage<ReceiverUserData[]>('recent-users', []);
  const [recentWallets, setRecentWallets] = useLocalStorage<ReceiverWalletData[]>('recent-wallets', []);
  const {
    data: quoteData,
    setParams,
    params,
    refetch: refetchWithdrawQuote,
    isLoading,
    isFetching,
    enabled
  } = useWithdrawQuote();
  const quoteDataLoading = isLoading || isFetching;
  useEffect(() => {
    if (!isModalOpen && onClose) {
      onClose(false);
    }
  }, [isModalOpen, onClose]);
  useEffect(() => {
    console.log('withdrawQuote:quote', quoteData, quoteDataLoading, isLoading, isFetching, params);
  }, [quoteData, quoteDataLoading]);
  const tokenUserBalance = assetData?.balances?.find(asset => {
    return asset.contract_address === tokenData.address && asset?.network === tokenData.network;
  });
  const sponsorUSD = quoteData?.fees?.sponsorUsd;
  const gasUsd = quoteData?.fees?.gasFeeUsd;
  const form = useForm<SendFundsSchemaInputs>({
    resolver: zodResolver(sendFundsSchema),
    mode: 'onChange',
    defaultValues: {
      tokenAmount: '0',
      tokenUsdAmount: '0',
      quoteParams: {},
      transferParams: {},
      sendTo: ''
    }
  });
  const _watchSendTo = form.watch('sendTo');
  const _watchTokenAmount = form.watch('tokenAmount');
  const _watchTokenUsdAmount = form.watch('tokenUsdAmount');
  const _watchTransferParams = form.watch('transferParams');
  const _watchForm = form.watch();
  const {
    mutateWithdrawOrderData,
    mutateWithdrawOrder,
    isPendingMutateWithdrawOrder,
    isSuccessMutateWithdrawOrder,
    onErrorMutateWithdrawOrder
  } = useWithdrawAsset({
    // @ts-expect-error - TODO: fix this
    transferParams: form.getValues('transferParams'),
    // @ts-expect-error - TODO: fix this
    quoteParams: form.getValues('quoteParams')
  });
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    form.reset();
    setStage(StagesSendFunds.amount);
    setIsCheckingInput(false);
  };
  const _handleOnSelectReceiver = async (receiver: SendFundsSchemaInputs['sendTo']) => {
    setIsCheckingInput(prevValue => !prevValue);
    const _validateInputs = sendFundsSchema.shape.sendTo.safeParse(receiver);
    if (_validateInputs.error) {
      console.log('_handleOnSelectReceiver:_validateInputs:error', {
        receiver,
        err: _validateInputs.error
      });
      form.setError('sendTo', {
        message: _validateInputs.error.issues[0].message
      }, {
        shouldFocus: true
      });
      setIsCheckingInput(prevValue => !prevValue);
      return;
    }
    if (receiver === PKP.username) {
      form.setError('sendTo', {
        message: 'You can not transfer to yourself.'
      }, {
        shouldFocus: true
      });
      setIsCheckingInput(prevValue => !prevValue);
      return;
    }
    try {
      if (receiver.startsWith('@')) {
        console.log('_handleOnSelectReceiver:receiver is username', receiver);
        const _userData = await _userNameData(receiver);
        console.log('_handleOnSelectReceiver:username', {
          receiver,
          _userData
        });
        if (_userData.status === 404) {
          form.setError('sendTo', {
            message: 'Please enter a valid Genius Username'
          }, {
            shouldFocus: true
          });
        }
        if (_userData.status === 200) {
          const _res = await _userData.json();
          console.log('onSubmit:username', {
            receiver,
            _res,
            recent: recentUsers,
            match: recentUsers.some(user => user.username === receiver)
          });
          const _newRecentUser: ReceiverUserData = {
            username: receiver,
            pfp: _res.pfp,
            first_name: _res.first_name,
            last_name: _res.last_name,
            eth_address: _res.eth_address,
            solana_address: _res.solana_address,
            pkp: _res.pkp
          };
          setRecentUsers(prev => recentUsers.some(user => user.username === receiver) ? [...prev] : [...prev, _newRecentUser]);
          setReceiver(_newRecentUser);
          const _receiverAddress = tokenData.network === ChainId.SOLANA ? _res.solana_address : _res.eth_address;
          console.log('onSubmit:usernamesss', _newRecentUser, _receiverAddress);
          form.setValue('sendTo', _receiverAddress);
          setIsCheckingInput(prevValue => !prevValue);
          if (stage !== StagesSendFunds.verify) setStage(StagesSendFunds.verify);
          return;
        }
        setIsCheckingInput(prevValue => !prevValue);
        return;
      }
      if (onlyUsername) {
        console.log('_handleOnSelectReceiver:ONLY USERNAMES ALLOWED', receiver, onlyUsername);
        form.setError('sendTo', {
          message: 'Please input a valid Genius username.'
        });
        setIsCheckingInput(prevValue => !prevValue);
        return;
      }
      console.log('_handleOnSelectReceiver:wallet', validateSolAddress(receiver), tokenData.network == ChainId.SOLANA && validateSolAddress(receiver) == false);
      if (!ethers.utils.isAddress(receiver) && !validateSolAddress(receiver)) {
        form.setError('sendTo', {
          message: 'Please enter a valid wallet address.'
        });
        form.setValue('sendTo', receiver);
        setIsCheckingInput(prevValue => !prevValue);
        return;
      }
      if (tokenData.network == ChainId.SOLANA && validateSolAddress(receiver) == false) {
        form.setError('sendTo', {
          message: 'Please enter a valid solana address.'
        });
        form.setValue('sendTo', receiver);
        setIsCheckingInput(prevValue => !prevValue);
        return;
      }
      if (tokenData.network != ChainId.SOLANA && !ethers.utils.isAddress(receiver)) {
        form.setError('sendTo', {
          message: 'Please enter a valid EVM wallet address.'
        });
        form.setValue('sendTo', receiver);
        setIsCheckingInput(prevValue => !prevValue);
        return;
      }
      const _newRecentWallet = {
        address: receiver,
        network: tokenData.network
      };
      setRecentWallets(prev => recentWallets.some(wallet => wallet.address === receiver) ? [...prev] : [...prev, _newRecentWallet]);
      setReceiver(_newRecentWallet);
      form.setValue('sendTo', receiver);
      setIsCheckingInput(prevValue => !prevValue);
      if (stage !== StagesSendFunds.verify) setStage(StagesSendFunds.verify);
      return;
    } catch (error) {
      toast.error('Something went wrong, try again');
      setIsCheckingInput(prevValue => !prevValue);
      console.log('_handleOnSelectReceiver:ERROR', {
        error
      });
    }
  };
  const handleSelectTokenAmount = () => {
    if (_watchTokenAmount === '0' || _watchTokenAmount === null) {
      toast.info('Amount should be greater than 0');
      return;
    }
    setStage(StagesSendFunds.selectUser);
  };
  const _resetSendTo = () => {
    console.log('_resetSendTo', _watchSendTo);
    form.resetField('sendTo');
    setStage(StagesSendFunds.selectUser);
    return;
  };
  const _resetAmount = () => {
    form.resetField('tokenAmount');
    return;
  };
  const onSubmit = async (values: SendFundsSchemaInputs) => {
    console.log('onSubmit', {
      values,
      tokenData,
      assetData
    });
    mutateWithdrawOrder();
  };
  const _formIsValid = useMemo(() => {
    const _validateFields = sendFundsSchema.safeParse(_watchForm);
    if (_validateFields.error) {
      return false;
    }
    if (_validateFields.success) {
      console.log('SendFunds:_formIsValid:SUCCESS', {
        receiver,
        _watchForm
      });
      return true;
    }
  }, [_watchForm]);
  useEffect(() => {
    const [_validSendTo, _validTokenAmount] = [sendFundsSchema.shape.sendTo.safeParse(_watchSendTo), sendFundsSchema.shape.tokenAmount.safeParse(_watchTokenAmount)];
    if (_validSendTo.success && _validTokenAmount.success) {
      getQuoteAndTransferParams({
        tokenUserBalance,
        PKP,
        getEVMWallet,
        form,
        setParams,
        refetch: refetchWithdrawQuote,
        _formTokenAmount: _watchTokenAmount,
        _formSendTo: _watchSendTo,
        _formTokenUsdAmount: _watchTokenUsdAmount
      });
    }
    return () => {};
  }, [_watchSendTo, _watchTokenAmount]);
  useEffect(() => {
    if (quoteData === undefined) {
      refetchWithdrawQuote();
      return;
    }
    if (quoteData.quoteId) {
      console.log('SendFunds:useEffect:quoteData', {
        quoteData,
        _watchTransferParams
      });
      form.setValue('quoteParams', quoteData);
    }
    return () => {};
  }, [quoteData]);
  useEffect(() => {
    if (onErrorMutateWithdrawOrder) {
      if (stage !== StagesSendFunds.error) {
        setStage(StagesSendFunds.error);
        return;
      }
    }
    if (isSuccessMutateWithdrawOrder) {
      if (stage !== StagesSendFunds.success) {
        setStage(StagesSendFunds.success);
        return;
      }
    }
    if (isPendingMutateWithdrawOrder) {
      if (stage !== StagesSendFunds.isLoading) {
        setStage(StagesSendFunds.isLoading);
        return;
      }
    }
  }, [isPendingMutateWithdrawOrder, onErrorMutateWithdrawOrder, isSuccessMutateWithdrawOrder]);
  if (tokenUserBalance) return <div>
        <div className='ta-c'>
          {onlyUsername ? <Button onClick={toggleModal} type='button' className={`w-[98px] h-[56px] text-[14px] rounded-full text-genius-cream justify-center bg-genius-blue ${isOpen ? 'hidden' : ''}`}>
              Send
              <PiPaperPlaneTilt className='w-4 h-4' />
            </Button> : <Button onClick={toggleModal} type='button' variant='ghost' className={`w-full h-[56px] text-[14px] bg-genius-blue rounded-full text-genius-cream flex gap-2 items-center ${isOpen ? 'hidden' : ''}`}>
              Send
              <PiPaperPlaneTilt className='w-4 h-4' />
            </Button>}
        </div>
        <SlidingModal isOpen={isModalOpen} onClose={toggleModal} heightFit={true}>
          <div className='mt-2 grid justify-items-center relative'>
            {stage == StagesSendFunds.isLoading && <SwapLoader className='pt-0 mt-0 w-full' />}
            {stage == StagesSendFunds.success && <>
                <SuccessStage />
                <Button type='button' variant='blue' className='h-[56px] mt-2 rounded-full w-[85%]' onClick={() => window.open(`
                      ${explorers[tokenData.network]}${mutateWithdrawOrderData?.data?.result?.decodedResponse?.transferTransaction?.hash}`, '_blank')}>
                  View Confirmation
                </Button>
                <Button type='button' className='h-[56px] rounded-full bg-genius-pink md:hover:bg-genius-pink w-[85%]' onClick={() => toggleModal()}>
                  Done
                </Button>
              </>}
            {stage == StagesSendFunds.error && <>
                <ErrorStage />
                <Button type='button' className='h-[56px] rounded-full bg-genius-pink md:hover:bg-genius-pink w-[85%]' onClick={() => {
            form.reset();
            setStage(StagesSendFunds.amount);
            return;
          }}>
                  Try Again
                </Button>
              </>}
            {Boolean(stage === StagesSendFunds.amount || stage === StagesSendFunds.selectUser || stage === StagesSendFunds.verify) && <>
                {stage !== StagesSendFunds.amount && <div className='absolute left-4 top-[-7px]'>
                    <div onClick={() => {
              if (stage === StagesSendFunds.selectUser) {
                setStage(StagesSendFunds.amount);
                return;
              }
              if (stage === StagesSendFunds.verify) {
                _resetSendTo();
                return;
              }
            }}>
                      <div className='text-[14px] flex items-center bg-genius-blue rounded-full h-[31px] w-[74px]'>
                        <IoIosArrowBack className={`ml-2 mr-2 w-[16px] text-genius-cream`} width={20} height={18} />{' '}
                        <p>Back</p>
                      </div>
                    </div>
                  </div>}
                <div className='text-center text-[24px] mb-6'>Send Funds</div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className='w-full'>
                    {stage === StagesSendFunds.amount && <div className='px-1 justify-center ta-c pb-6'>
                        <KeypadController tokenUserBalance={tokenUserBalance} form={form} />
                        <Button type='button' className='h-[56px] rounded-full bg-genius-pink md:hover:bg-genius-pink w-[95%] mt-[40px] ta-c justify-center ' disabled={isCheckingInput || _watchTokenAmount === '0' || _watchTokenUsdAmount === '0'} onClick={() => handleSelectTokenAmount()}>
                          {isCheckingInput && <Loader2 className='animate-spin w-4 h-4' />}
                          {isCheckingInput ? 'Loading' : _watchTokenAmount === '0' || _watchTokenUsdAmount === '0' ? 'Choose the amount' : 'Next'}
                        </Button>
                      </div>}
                    {stage === StagesSendFunds.selectUser && <div className='pr-4 pl-2'>
                        <FormField control={form.control} name='sendTo' render={({
                  field
                }) => <FormItem className='px-4'>
                              <FormControl>
                                <>
                                  <Input {...field} className='border-none bg-genius-blue text-[14px] h-[38px]' disabled={isCheckingInput} placeholder={onlyUsername ? '@username' : `@username or ${tokenData.network == ChainId.SOLANA ? 'Solana' : 'EVM'} wallet`} onKeyDown={e => {
                        if (e.code === 'Enter') {
                          e.preventDefault();
                          _handleOnSelectReceiver(_watchSendTo);
                          return;
                        }
                      }} />
                                  <UserSearchResults value={_watchSendTo} handleOnSelectUser={_handleOnSelectReceiver} />
                                </>
                              </FormControl>
                              <FormMessage />
                            </FormItem>} />
                        <RecentTransfers onFormSubmit={{
                  form: form,
                  setReceiver,
                  tokenNetwork: tokenData.network
                }} setStage={setStage} onlyUsername={onlyUsername} />

                        <Button type='button' onClick={() => _handleOnSelectReceiver(_watchSendTo)} className='h-[56px] absolute -bottom-16 rounded-full bg-genius-pink md:hover:bg-genius-pink w-[95%] mb-16 flex items-center' disabled={!enabled}>
                          {isCheckingInput && <Loader2 className='animate-spin w-4 h-4' />}
                          {isCheckingInput ? 'Loading' : _watchSendTo === '' ? 'Choose a receipient' : !enabled ? `Please provide a valid ${tokenData?.network == ChainId.SOLANA ? 'Solana' : 'EVM'} address` : 'Next'}
                        </Button>
                      </div>}
                    {stage === StagesSendFunds.verify && <div className='h-[440px]'>
                        <VerifyStage usdAmount={_watchTokenUsdAmount} tokenAmount={_watchTokenAmount} receiver={receiver} setStage={setStage} tokenData={tokenUserBalance} resetSendTo={_resetSendTo} />
                        {sponsorUSD ? <p className='text-genius-cream/50 px-8 mt-4 w-full justify-between text-xs'>
                            You will receive ${toSpecialFixed(sponsorUSD, 2).value} USD in native
                            token from your token transfer for future transactions.
                          </p> : <></>}
                        {gasUsd ? <p className='text-genius-cream/50 px-8 mt-4 w-full justify-between text-xs'>
                            Gas is estimated to cost ${toSpecialFixed(gasUsd, 2).value} USD for this
                            transfer
                          </p> : <></>}
                        {quoteData?.error ? <div className='px-8 mt-12 text-center text-sm text-genius-cream/50'>
                            {' '}
                            Unable to prepare a transfer transaction. Please increase your amount
                            and try again.
                          </div> : <FixedBtnWrapper className='bottom-[0px!important] pb-[0px]!important'>
                            <Button disabled={!_formIsValid || quoteDataLoading} type='submit' className='m-auto my-6 h-[56px] rounded-full bg-genius-pink md:hover:bg-genius-pink w-[95%] flex gap-2'>
                              {(!_formIsValid || quoteDataLoading) && <Loader2 className='animate-spin w-4 h-4' />}
                              {!_formIsValid || quoteDataLoading ? 'Preparing...' : 'Confirm'}
                            </Button>
                          </FixedBtnWrapper>}
                      </div>}
                  </form>
                </Form>
              </>}
          </div>
        </SlidingModal>
      </div>;
};
export default SendFundsController;